import React, { useEffect, useState } from 'react';
import organizationsService, { Organization } from '../../services/crm/organizationsService';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import organizationMembersService from '../../services/crm/organizationMembersService';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmRoleStore } from '../../store/crmRole.store';
import { Link } from 'react-router-dom';

export interface CRMHeaderProps {
  title?: string;
  subtitle?: string;
  showNavigation?: boolean;
  rightSlot?: React.ReactNode;
}

const CRMHeader: React.FC<CRMHeaderProps> = ({
  title = 'CRM',
  subtitle = 'Manage your workspace data',
  showNavigation = true,
  rightSlot,
}) => {
  const { activeOrgId, setActiveOrgId, isBootstrapped, setBootstrapped } = useCrmOrgStore();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { setRole } = useCrmRoleStore();

  const ensureOrg = async () => {
    setLoading(true); setError(null);
    try {
      const resp = await organizationsService.list();
      const list = resp.data || [];
      if (list.length === 0) {
        const created = await organizationsService.create('My Organization');
        setOrgs([created.data]);
        setActiveOrgId(created.data.id);
      } else if (list.length === 1) {
        setOrgs(list);
        setActiveOrgId(list[0].id);
      } else {
        setOrgs(list);
        if (!activeOrgId) setActiveOrgId(list[0].id);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
      setBootstrapped(true);
    }
  };

  useEffect(() => {
    ensureOrg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When activeOrgId changes, load current user's role and store it
  useEffect(() => {
    (async () => {
      try {
        if (!activeOrgId || !user?.id) { setRole(null); return; }
        const list = await organizationMembersService.list(activeOrgId);
        const me = (list.data || []).find(m => m.user_id === user.id);
        setRole((me?.role as any) || null);
      } catch {
        setRole(null);
      }
    })();
  }, [activeOrgId, user?.id, setRole]);

  return (
    <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
        {showNavigation && (
          <nav className="flex flex-wrap items-center gap-2 text-sm">
            <Link to="/crm/dashboards" className="px-2 py-1 rounded hover:bg-gray-100">Dashboards</Link>
            <Link to="/crm#activities" className="px-2 py-1 rounded hover:bg-gray-100">Activities</Link>
            <Link to="/crm/calendar" className="px-2 py-1 rounded hover:bg-gray-100">Calendar</Link>
            <Link to="/crm#deals" className="px-2 py-1 rounded hover:bg-gray-100">Deals</Link>
            <Link to="/crm/workflows" className="px-2 py-1 rounded hover:bg-gray-100">Workflows</Link>
          </nav>
        )}
        {rightSlot}
        {loading && !isBootstrapped ? (
          <span className="text-sm text-gray-500">Loading org…</span>
        ) : error ? (
          <button
            type="button"
            className="text-sm text-red-600 underline"
            onClick={ensureOrg}
          >
            {error} — retry
          </button>
        ) : (
          orgs.length > 1 ? (
            <select
              className="border rounded p-2"
              value={activeOrgId || ''}
              onChange={e => setActiveOrgId(e.target.value || null)}
              aria-label="Select organization"
            >
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          ) : (
            <span className="text-sm text-gray-600">{orgs[0]?.name || 'My Organization'}</span>
          )
        )}
      </div>
    </div>
  );
};

export default CRMHeader;
